/*
 * Master Stage 8 (3.2) — MySchoolRx Website CMS.
 * Vertical tablist; the URL hash selects the tab. Each tab mounts an EXISTING
 * editor unchanged, all writing their existing records via their existing APIs:
 *  - Landing copy / FAQ  → MySchoolRx product record (one endpoint)
 *  - Get Started CTA     → Landing Page screen
 *  - Pilot Program       → Pilot settings
 *  - Experts             → the single Experts editor (same product record)
 *  - Message Bar         → Message bar settings
 */
import React, { useEffect, useState } from "react";
import { Globe, ExternalLink } from "lucide-react";
import { ProductCatalog } from "./ProductsAdminPage";
import LandingSettingsPage from "./LandingSettingsPage";
import PilotSettings from "../PilotSettings";
import MessageBarSettings from "../MessageBarSettings";
import ExpertsAdminPage from "./ExpertsAdminPage";

const TABS = [
  { key: "landing", hash: "#landing", label: "Landing copy", testid: "cms-tab-landing" },
  { key: "get-started", hash: "#get-started", label: "Get Started CTA", testid: "cms-tab-get-started" },
  { key: "pilot", hash: "#pilot", label: "Pilot Program", testid: "cms-tab-pilot" },
  { key: "faq", hash: "#faq", label: "FAQ", testid: "cms-tab-faq" },
  { key: "experts", hash: "#experts", label: "Experts", testid: "cms-tab-experts" },
  { key: "message-bar", hash: "#message-bar", label: "Message Bar", testid: "cms-tab-message-bar" },
];

const hashToKey = (h) => {
  const found = TABS.find((t) => t.hash === h);
  return found ? found.key : "landing";
};

export default function MsxCmsPage({ session }) {
  const [active, setActive] = useState(() => hashToKey(window.location.hash));

  useEffect(() => {
    const onHash = () => setActive(hashToKey(window.location.hash));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const select = (t) => {
    setActive(t.key);
    if (window.location.hash !== t.hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${t.hash}`);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="msx-cms">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] text-white" style={{ backgroundColor: "#007a9e" }}>
          <Globe className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11.5px] font-bold tracking-[0.14em] uppercase text-[#00698a]">MySchoolRx · Website CMS</div>
          <h1 className="text-2xl font-bold text-[#111827]">Website CMS</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_260px] gap-6">
        {/* Vertical tablist */}
        <nav
          role="tablist"
          aria-orientation="vertical"
          aria-label="Website CMS sections"
          className="flex lg:flex-col gap-1.5 flex-wrap"
        >
          {TABS.map((t) => {
            const on = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={on}
                onClick={() => select(t)}
                data-testid={t.testid}
                className={`text-left px-3.5 py-2 rounded-lg text-[13px] font-semibold border transition-colors ${
                  on
                    ? "bg-[#e6f4f8] text-[#00698a] border-[#b3dce7]"
                    : "bg-white text-[#374151] border-[#e5e7eb] hover:bg-[#f9fafb]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Active panel */}
        <div data-testid={`msx-cms-panel-${active}`}>
          {active === "landing" && (
            <ProductCatalog session={session} productType="caporilli_diagnostic" variant="landing" defaultOpen />
          )}
          {active === "get-started" && <LandingSettingsPage session={session} />}
          {active === "pilot" && <PilotSettings session={session} />}
          {active === "faq" && (
            <ProductCatalog session={session} productType="caporilli_diagnostic" variant="faq" defaultOpen />
          )}
          {active === "experts" && <ExpertsAdminPage session={session} />}
          {active === "message-bar" && <MessageBarSettings session={session} />}
        </div>

        {/* Where this appears */}
        <aside className="hidden lg:block">
          <div className="rounded-xl border border-[#e5e7eb] bg-[#f9fafb] p-4 sticky top-16">
            <div className="text-[11.5px] font-bold tracking-[0.1em] uppercase text-[#6b7280]">Where this appears</div>
            <div className="mt-1.5 text-[13px] text-[#374151] font-mono">/myschoolrx</div>
            <a
              href="/myschoolrx"
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#007a9e] hover:underline"
              data-testid="msx-cms-open-landing"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open the landing page
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}
